import * as React from 'react'
import {
  makeStyles,
  createStyles,
  Theme,
} from 'cs-kevlar-react/lib/es/theming/theme'
import { Accordion, Box, Grid, Spacing } from 'cs-kevlar-react'
import { EditableUser } from '../../contexts/base'
import { AlertErrorHeader, EditModalActionBar, ErrorDetail } from '..'
import { CustomizableInput } from './CustomizableInput'

interface AddMultipleUsersDetailsProps {
  onAdd: (values: EditableUser[]) => void
  onClose: () => void
  users: EditableUser[]
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    gridItem: {
      width: '100%',
    },
    spacer: {
      flexGrow: 1,
      minHeight: 0,
    },
    usersContainer: {
      flexGrow: 1,
      minHeight: 0,
      overflowY: 'auto',
    },
  }),
)

const phoneRE = /^(\+\d{1,2}\s)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/
//The source of this email regular expression is https://emailregex.com/. It claims to be 99.99% effective.
const emailRE = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/

export const AddMultipleUsersDetails: React.FunctionComponent<AddMultipleUsersDetailsProps> = ({
  onAdd,
  onClose,
  users,
}) => {
  const classes = useStyles({})

  const [showErrorBanner, setShowErrorBanner] = React.useState(false)
  const [errorMessage, setErrorMessage] = React.useState('')
  const [errors, setErrors] = React.useState<ErrorDetail[]>([])

  const onAddUsers = (users: EditableUser[]) => {
    const errors: ErrorDetail[] = []

    users.forEach((user) => 
    {
      if (user.phone == null || user.phone == '')
      {
        var error: ErrorDetail = {
          errorIdentifierText: user.fullName,
          error: 'Phone is required',
          id: user.id
        }

        errors.push(error)
      }
      else if (!phoneRE.test(user.phone))
      {
        var error: ErrorDetail = {
          errorIdentifierText: user.fullName,
          error: 'Invalid phone',
          id: user.id
        }

        errors.push(error)
      }

      if (user.email == null || user.email == '')
      {
        var error: ErrorDetail = {
          errorIdentifierText: user.fullName,
          error: 'Email is required',
          id: user.id
        }

        errors.push(error)
      }
      else if (!emailRE.test(user.email))
      {
        var error: ErrorDetail = {
          errorIdentifierText: user.fullName,
          error: 'Invalid email',
          id: user.id
        }

        errors.push(error)
      }
    })

    if (errors.length == 0)
    {
      onAdd(users)
    }
    else
    {
      setShowErrorBanner(errors.length > 0)
      setErrorMessage(errors.length + ' validation error' + (errors.length > 1 ? 's' : '') + ' occurred')
      setErrors(errors)
    }
  }

  const handlePhone = (value: any, user: EditableUser) => {
    user.phone = value.target.value
  }

  const handleEmail = (value: any, user: EditableUser) => {
    user.email = value.target.value
  }
 
  return (
    <React.Fragment>
     {showErrorBanner && 
       <AlertErrorHeader errorLabel='Name' errorMessage={errorMessage} errors={errors} />
     }
     <Box className={classes.usersContainer}>
       {users.map((user) => {
         return (
           <Accordion
             id={user.id}
             key={user.id}
             initiallyExpanded
             title={user.fullName}
           >
             <Grid
               container
               spacing={Spacing}
               direction="column"
               alignItems="flex-start"
               justifyContent="center"
             >
               <Grid item className={classes.gridItem}>
                 <CustomizableInput 
                   defaultValue={user.name} 
                   name='Username'
                   readOnly={true}
                   showLabel={true}
                 />
               </Grid>
               <Grid item className={classes.gridItem}>
                 <CustomizableInput 
                   defaultValue={user.phone} 
                   errorRegEx={phoneRE} 
                   name='Phone' 
                   onChange={(event)=> {handlePhone(event, user)}}
                   required={true}
                   showLabel={true}
                 />
               </Grid>
               <Grid item className={classes.gridItem}>
                 <CustomizableInput 
                   defaultValue={user.email} 
                   errorRegEx={emailRE} 
                   name='Email' 
                   onChange={(event)=> {handleEmail(event, user)}}
                   required={true}
                   type='email'
                   showLabel={true}
                 />
               </Grid>
             </Grid>
           </Accordion>
         );
       })}
     </Box>
     <Box className={classes.spacer} />
     <EditModalActionBar
       canDelete={false}
       onCancel={onClose}
       onSave={() => onAddUsers(users)}          
       saveButtonType='button'
       saveText='Add'
     />
   </React.Fragment>
  );
}
